import api, { getApiErrors } from '@app/services/api';
import type {
    AIAgentDocumentDeleteParams,
    AIAgentDocumentDeleteResponse,
    AIAgentDocumentUploadParams,
    AIAgentDocumentUploadResponse,
    ApiError,
} from '@app/services/api.types';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import useApp from '../useApp';

export const uploadAgentDocument = async (
    params: AIAgentDocumentUploadParams
): Promise<AIAgentDocumentUploadResponse> => {
    const url = apiEndpoints.aiAgents.uploadDocument(params.agentId);
    const formData = new FormData();
    formData.append('file', params.file);
    const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response?.data?.data;
};

export const deleteAgentDocument = async (
    params: AIAgentDocumentDeleteParams
): Promise<AIAgentDocumentDeleteResponse> => {
    const url = apiEndpoints.aiAgents.deleteDocument(params.agentId, params.documentId);
    const response = await api.delete(url);
    return response?.data?.data;
};

const useAgentDocumentActions = () => {
    const { message } = useApp();

    const uploadDocument = useMutation<
        AIAgentDocumentUploadResponse,
        AxiosError<ApiError>,
        AIAgentDocumentUploadParams
    >({
        mutationFn: uploadAgentDocument,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Document uploaded successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.error, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [
                ['ai-agent', 'single'],
                ['ai-agent', 'list'],
            ],
        },
    });

    const deleteDocument = useMutation<
        AIAgentDocumentDeleteResponse,
        AxiosError<ApiError>,
        AIAgentDocumentDeleteParams
    >({
        mutationFn: deleteAgentDocument,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Document removed successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.error, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [
                ['ai-agent', 'single'],
                ['ai-agent', 'list'],
            ],
        },
    });

    return { uploadDocument, deleteDocument };
};

export default useAgentDocumentActions;
